/**
 * 全編モーション (映像スタイル「モーショングラフィックス」・docs/features/brand-cut-motion-graphics-plan.md §10.5) の
 * シナリオ画面の操作:
 *   - 計画 (motion_plan) の状態 (曲に収まらない advice / 尺の割付が古い) の表示
 *   - 「BGM の小節に合わせて尺の割付を更新」(replan・ポイント消費なし)
 *   - 「動画を生成 [ポイント]」(旧表記「全編を生成」)(generate-whole・Phase 2f §10.6): 生成の主ルート。1 本のモーションとして作り、計画の境界でシーンごとに分割する
 *     (各シーンの「モーションを再生成」は全編を生成した後にだけ出す・SceneVideoBox)
 *   - 全編の「AI で修正」(revise)・「全編を再生」(全編の素材を素材プレビューで開く)
 * 計画の取得は react-query で共有する (BGM 欄のリンクとシーン一覧上のパネルで二重に取らない)。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import styles from './ScenarioGenerator.module.css';
import { useToast } from '../../../hooks/useToast';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { useCreditRates } from '../../../features/organizations/hooks';
import { replanScenarioMotion, updateScenarioMotionSettings, type MotionPlanAdvice, type MotionWholePreviewVersion, type MotionWholePreviews } from '../../../features/scenarios/api';
import { ToggleSwitch } from '../../common/ToggleSwitch';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { useSceneStateContext } from './hooks/SceneStateContext';
import { motionPlanQueryKey, useMotionPlan } from './hooks/useMotionPlan';
import { ModelDropdownItem, ModelDropdownMenuShell, ModelDropdownSectionHeader } from '../common/ModelDropdownMenu';
import { usePortalDropdown } from './hooks/usePortalDropdown';
import {
  MOTION_QUALITY_DESCRIPTIONS,
  MOTION_QUALITY_HEADING,
  MOTION_QUALITY_LABELS,
  MOTION_QUALITY_ROUTES,
  motionQualityFor,
  type MotionQuality,
  useMotionQuality,
} from '../../../features/motion/quality';
import { MotionModelSelect } from '../common/MotionModelSelect';
import {
  MOTION_REVISE_INSTRUCTION_MAX,
  motionReviseCreditRate,
  readMotionWhole,
  validateMotionReviseInstruction,
  wholeMotionBlockReason,
  deriveMotionPlanNotice,
  formatMotionPlanChanges,
  isMotionEngineScenario,
  motionPlanDurationPatch,
} from './motionScenario';
import type { Scenario } from './types';
import { isMotionV2Script } from '../../../features/scenarios/scriptContract';
import {
  MOTION_VOICE_FIRST_NOTICE,
  motionReviseVoiceBlockReason,
  scenesNeedingVoice,
  voiceStatusesFromMotionSync,
  voiceTargetScenes,
} from './voiceLines';

/** replan を実行し、変わった予定尺を画面の state にパッチする (DB は backend が書き戻し済み・再取得しない)。 */
function useMotionReplan(scenario: Scenario) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLastResult, updateSceneVideoSettings } = useSceneHandlers();
  const [busy, setBusy] = useState(false);
  const [advice, setAdvice] = useState<MotionPlanAdvice | null>(null);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await replanScenarioMotion(scenario.id);
      if (res.ok && res.planned) {
        setAdvice(null);
        const patch = motionPlanDurationPatch(res.changed);
        if (patch.size > 0) {
          setLastResult((prev) => (prev ? {
            ...prev,
            scenarios: prev.scenarios.map((s) => (s.id !== scenario.id ? s : {
              ...s,
              scenes: (s.scenes || []).map((sc) => (patch.has(sc.id) ? { ...sc, video_duration_seconds: patch.get(sc.id) } : sc)),
            })),
          } : prev));
          for (const sc of scenario.scenes || []) {
            const seconds = patch.get(sc.id);
            if (seconds != null) updateSceneVideoSettings(`${scenario.id}-scene-${sc.order}`, { duration: seconds }, { skipPersist: true });
          }
          const hasBgm = !!scenario.bgm_material_id && !scenario.bgm_excluded;
          toast.success(`${hasBgm ? 'BGM の小節に合わせて' : 'ナレーションの長さで'}尺の割付を更新しました\n${formatMotionPlanChanges(res.changed).join('\n')}`);
        } else {
          toast.show('各シーンの開始秒と尺を確認しました。前回の割付から変わっていません');
        }
      } else if (res.ok) {
        setAdvice(null);
        toast.show(res.reason || '小節で尺を割り付けられない曲のため、秒ベースで生成します');
      } else {
        setAdvice(res.advice ?? null);
        toast.error(res.advice?.message || res.reason || 'BGM の長さに台本が収まりません');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, '尺の割付の更新に失敗しました'));
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenario.id) });
    }
  };
  return { run, busy, advice, setAdvice };
}

function AdviceBox({ advice }: { advice: MotionPlanAdvice }) {
  // 本文 (backend の advice.message) にシーンごとの縮める秒数と BGM の延長まで書いてあるので、本文だけを出す
  return <span className={styles.motionSyncStaleNotice}>{advice.message}</span>;
}

/** 版の種類の表示名。 */
const PREVIEW_KIND_LABEL: Record<MotionWholePreviewVersion['kind'], string> = { draft: '初稿', revise: '修正' };

/**
 * 全編の生成の途中経過 (試写の 12 コマと採点) を版ごとに並べる (2026-09-29 利用者要望: 初稿の段階で捨てるか決めたい)。
 * 生成中は 5 秒ごとに増える。完了後も残し、採用した版に印を付ける。何も無ければ描かない。
 */
function MotionWholePreviewList({ previews, running }: { previews: MotionWholePreviews | null; running: boolean }) {
  const versions = previews?.versions ?? [];
  if (versions.length === 0 && !running) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="motion-whole-previews">
      <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.5)' }}>
        途中経過{versions.length === 0 ? ' (初稿の試写が終わるとここに並びます)' : ''}
      </span>
      {versions.map((v) => {
        const adopted = previews?.adoptedRound === v.round;
        return (
          <div key={v.round} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid={`motion-whole-preview-${v.round}`}>
            {v.contactSheetUrl ? (
              <a href={v.contactSheetUrl} target="_blank" rel="noreferrer" title="試写のコマを大きく見る" style={{ flexShrink: 0 }}>
                <img src={v.contactSheetUrl} alt={`版 ${v.round} の試写`} style={{ width: 160, borderRadius: 4, display: 'block' }} />
              </a>
            ) : (
              <span style={{ width: 160, flexShrink: 0, fontSize: 11, color: 'rgba(245, 241, 230, 0.35)' }}>コマなし</span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, minWidth: 0 }}>
              <span style={{ color: 'rgba(245, 241, 230, 0.8)' }}>
                版 {v.round} {PREVIEW_KIND_LABEL[v.kind] ?? v.kind}・平均 {v.average.toFixed(1)} 点{v.passed ? '・合格' : ''}
                {adopted && <span style={{ marginLeft: 6, color: 'rgba(245, 241, 230, 0.9)', fontWeight: 700 }}>採用</span>}
              </span>
              {v.summary && <span style={{ color: 'rgba(245, 241, 230, 0.55)' }}>{v.summary}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 枠線・塗りの無いテキストボタン (「全編を再生」)。下線は付けない */
const textButtonStyle = (busy: boolean): React.CSSProperties => ({
  fontSize: '13px', color: 'rgba(245, 241, 230, 0.7)', background: 'none', border: 'none', padding: '0 4px',
  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
});

const linkStyle = (busy: boolean): React.CSSProperties => ({
  fontSize: '12px', color: 'rgba(245, 241, 230, 0.5)', background: 'none', border: 'none', padding: 0,
  textAlign: 'left', cursor: busy ? 'default' : 'pointer', textDecoration: 'underline', opacity: busy ? 0.6 : 1,
});

/**
 * BGM 欄の境界丸めリンクの位置に出す「尺の割付を更新」(全編モーションのみ)。BGM があれば小節に合わせ、
 * 無ければナレーションの長さと予定尺で秒に割り付ける (2026-09-30 利用者要望「BGM なしでも生成できるように」)。
 */
export function MotionReplanLink({ scenario, disabled }: { scenario: Scenario; disabled?: boolean }) {
  const { run, busy, advice } = useMotionReplan(scenario);
  const hasBgm = !!scenario.bgm_material_id && !scenario.bgm_excluded;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        disabled={busy || disabled}
        title={hasBgm
          ? 'ナレーションの長さと BGM の小節頭から、各シーンの尺を解き直します (ポイント消費なし)'
          : 'ナレーションの長さと予定尺から、各シーンの尺を解き直します (ポイント消費なし)'}
        onClick={() => void run()}
        style={linkStyle(busy)}
      >
        {busy ? '尺の割付を更新中…' : hasBgm ? 'BGM の小節に合わせて尺の割付を更新' : 'ナレーションの長さで尺の割付を更新'}
      </button>
      {advice && <AdviceBox advice={advice} />}
    </div>
  );
}

/** 全編の「AI で修正」の指示入力 (ブランド管理の「AI で修正」と同じ見た目)。 */
function MotionReviseModal({
  rate, submitting, onSubmit, onClose,
}: {
  rate: number | null;
  submitting: boolean;
  onSubmit: (instruction: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const invalid = validateMotionReviseInstruction(text);
  return createPortal(
    <div className="modal-overlay modal-overlay--nested" onClick={(e) => e.stopPropagation()}>
      <div className="modal-card modal-card--sm">
        <div className="modal-header">
          <h2 className="modal-title">AI で修正 (全編)</h2>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる" disabled={submitting}>
            ×
          </button>
        </div>
        <div className={styles.motionReviseBody}>
          <p className={styles.motionReviseDesc}>
            全編のモーションへの修正の指示を書いてください。演出の骨格と配色・書体は保ったまま、指示した点だけを直した全編を作り、
            同じ境界でシーンごとに切り直して各シーンの動画を置き換えます (元の全編の素材は残ります)。
          </p>
          <textarea
            className={styles.motionReviseTextarea}
            value={text}
            maxLength={MOTION_REVISE_INSTRUCTION_MAX}
            rows={4}
            placeholder="例: 全体の配色をもっと落ち着いたトーンにして、シーン 3 の見出しは大きく出す"
            onChange={(e) => setText(e.target.value)}
            disabled={submitting}
            autoFocus
          />
          <span className={styles.motionReviseCost}>
            {rate === null ? '料金が設定されていないため現在ご利用いただけません' : `${rate} ポイント / 回`}
            {`・${text.trim().length} / ${MOTION_REVISE_INSTRUCTION_MAX} 字`}
          </span>
          <div className={styles.motionReviseFooter}>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={submitting}>キャンセル</button>
            <button
              type="button"
              className="btn-modal"
              onClick={() => onSubmit(text)}
              disabled={submitting || rate === null || !!invalid}
            >
              {submitting ? '送信中…' : '修正する'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** シーン一覧の上に置くパネル (全編モーションのシナリオだけ)。 */
export function MotionScenarioPanel({ scenario }: { scenario: Scenario }) {
  const {
    handleGenerateWholeMotion, handleReviseWholeMotion, wholeMotionRunning, wholeMotionStage,
    motionSyncStatus, fetchMaterialById, setPreviewModalMaterial,
  } = useSceneHandlers();
  const { sceneVideoLoading } = useSceneStateContext();
  const { data: creditRatesData } = useCreditRates();
  const toast = useToast();
  const queryClient = useQueryClient();
  // 全編の生成中は途中経過 (試写のコマ・採点) を追うため 5 秒ごとに取り直す
  const { data: plan } = useMotionPlan(scenario, { refetchIntervalMs: wholeMotionRunning?.[scenario.id] ? 5000 : false });
  const replan = useMotionReplan(scenario);
  // エキスパンドの開閉 (既定は開いた状態)
  const [expanded, setExpanded] = useState(true);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wholeMessage, setWholeMessage] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [generateAdvice, setGenerateAdvice] = useState<MotionPlanAdvice | null>(null);
  // 「BGM の拍に合わせる」トグル (generation_metadata.motion_beat_sync)。保存中は押せない
  const [beatSyncSaving, setBeatSyncSaving] = useState(false);
  const handleBeatSyncChange = async (next: boolean) => {
    if (beatSyncSaving) return;
    setBeatSyncSaving(true);
    try {
      const res = await updateScenarioMotionSettings(scenario.id, { beat_sync: next });
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenario.id) });
      if (res.replan.ok && res.replan.planned) {
        toast.success(next ? 'BGM の拍に合わせて尺を割り付け直しました' : '拍に合わせず、ナレーションの長さで尺を割り付け直しました');
      } else if (!res.replan.ok) {
        toast.error(res.replan.advice?.message || res.replan.reason || '尺を割り付けられません');
      } else {
        toast.show(res.replan.reason || '設定を保存しました');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, '設定の保存に失敗しました'));
    } finally {
      setBeatSyncSaving(false);
    }
  };

  if (!isMotionEngineScenario(scenario)) return null;

  const reviseRate = motionReviseCreditRate(creditRatesData?.rates);
  const wholeRunning = wholeMotionRunning?.[scenario.id] ?? null;
  // 全編ジョブの生成中フラグは各シーンにも立つので、シーン単位の生成中は全編のジョブが無いときだけ数える
  const anyRunning = !!wholeRunning
    || Object.entries(sceneVideoLoading).some(([k, v]) => v && k.startsWith(`${scenario.id}-scene-`));
  const notice = deriveMotionPlanNotice(plan, motionSyncStatus?.[scenario.id]);
  const hasBgm = !!scenario.bgm_material_id && !scenario.bgm_excluded;
  const shownAdvice = generateAdvice ?? replan.advice;
  // 品質モード (ライト / スタンダード / ダメ出し・全編の既定はライト): ライト ('speed') は 1 本の定額 (credits.wholeSpeed)。
  // 料金の行が未設定 (null・古い backend) ならライトは選べずスタンダードに寄せる
  // 選択は「動画を生成」の ▼ (シーンの「動画を生成」のモデル選択と同じ形・利用者指示 2026-10-01)
  const [storedQuality, setStoredQuality] = useMotionQuality('whole');
  const wholeSpeedCredits = plan?.credits?.wholeSpeed ?? null;
  const wholeQualityOptions = MOTION_QUALITY_ROUTES.whole.options;
  const wholeQualities: readonly MotionQuality[] = wholeSpeedCredits != null ? wholeQualityOptions : wholeQualityOptions.filter((q) => q !== 'speed');
  const wholeQuality = motionQualityFor(storedQuality, wholeQualities, MOTION_QUALITY_ROUTES.whole.defaultQuality);
  const creditsFor = (q: MotionQuality) => (q === 'speed' ? wholeSpeedCredits : (plan?.credits?.whole ?? null));
  const wholeCredits = creditsFor(wholeQuality);
  const qualityDropdown = usePortalDropdown({
    triggerClassName: styles.videoModelDropdownTrigger,
    groupClassName: styles.videoGenerateGroup,
    align: 'right',
  });
  const whole = readMotionWhole(scenario, plan);
  const blockReason = wholeMotionBlockReason({ plan });
  const wholeSupported = !!handleGenerateWholeMotion;
  // 声の台本 (motion-v2・§10.7.5): 声が全シーンで生成済みでなければ「声を先に作る」を勧める (必須にはしない・未決 E)。
  // 声が映像の生成時から変わったシーンがあれば「AI で修正」は backend も 400 で断るので押させない (§10.7.4)
  const voiceScript = isMotionV2Script(scenario);
  const syncStatuses = motionSyncStatus?.[scenario.id];
  const voicePending = voiceScript ? scenesNeedingVoice(scenario, voiceStatusesFromMotionSync(syncStatuses)) : [];
  const voiceFirstNotice = voiceScript && voiceTargetScenes(scenario).length > 0 && voicePending.length > 0
    ? MOTION_VOICE_FIRST_NOTICE
    : null;
  const reviseVoiceBlock = motionReviseVoiceBlockReason(syncStatuses, scenario.scenes);

  // 確認モーダルは出さずにそのまま生成する (料金・仕上げ方はボタンと ▼ に出ている・利用者指示 2026-10-02)
  const handleWholeGenerate = async () => {
    if (!handleGenerateWholeMotion) return;
    setSubmitting(true);
    try {
      const res = await handleGenerateWholeMotion(scenario, wholeQuality);
      setGenerateAdvice(!res.ok && res.advice ? res.advice : null);
      setWholeMessage(!res.ok && !res.advice && res.message ? res.message : null);
    } finally {
      setSubmitting(false);
      void queryClient.invalidateQueries({ queryKey: motionPlanQueryKey(scenario.id) });
    }
  };

  const handleReviseSubmit = async (instruction: string) => {
    if (!handleReviseWholeMotion) return;
    setSubmitting(true);
    try {
      const res = await handleReviseWholeMotion(scenario, instruction);
      if (res.ok) {
        setReviseOpen(false);
        setWholeMessage(null);
      } else if (res.message) {
        setWholeMessage(res.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlayWhole = async () => {
    if (!whole || opening) return;
    setOpening(true);
    try {
      const material = await fetchMaterialById(whole.materialId);
      if (material) setPreviewModalMaterial(material);
      else toast.error('全編の素材が見つかりませんでした');
    } catch (err) {
      toast.error(getErrorMessage(err, '全編の素材を開けませんでした'));
    } finally {
      setOpening(false);
    }
  };

  return (
    // ナレーション欄と同じエキスパンド (利用者指示 2026-09-29): 見出し行は「動画を生成」だけ、
    // 全編の再生 / AI で修正 / 尺の割付を更新と注意は開いた中に置く。既定は開いた状態
    <div className={styles.flowStepSection} style={{ marginBottom: 12 }}>
      <div
        className={styles.stepHeader}
        onClick={() => setExpanded((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.stepTitle}>
          <span
            className={styles.expandTriangle}
            style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
          >
            <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>モーショングラフィックス専用モード</span>
        </div>
        <div className={styles.bgmHeaderRight} onClick={(e) => e.stopPropagation()}>
          {wholeSupported && (
            <div className={`${styles.videoGenerateGroup} ${wholeRunning ? 'ai-loading-active' : ''}`}>
              <button
                type="button"
                className={styles.sceneVideoButton}
                disabled={!!blockReason || anyRunning || submitting}
                title={blockReason ?? '全編を 1 本のモーションとして作り、シーンごとに分割します'}
                onClick={() => void handleWholeGenerate()}
              >
                {wholeRunning === 'generate'
                  ? '動画を生成中…'
                  : wholeRunning === 'revise'
                    ? '動画を修正中…'
                    : (
                      <>
                        {whole ? '動画を再生成' : '動画を生成'}
                        {wholeCredits != null && <span className={styles.creditBadge}>{wholeCredits}</span>}
                      </>
                    )}
              </button>
              <div className={styles.videoModelDropdown}>
                <button
                  type="button"
                  className={styles.videoModelDropdownTrigger}
                  disabled={anyRunning || submitting}
                  onClick={(e) => qualityDropdown.open('motion-whole-quality', e.currentTarget)}
                  data-tooltip={`${MOTION_QUALITY_HEADING}: ${MOTION_QUALITY_LABELS[wholeQuality]}`}
                  data-tooltip-delay=""
                  data-testid="motion-whole-quality-trigger"
                >
                  <span className={styles.videoModelArrow}>▼</span>
                </button>
              </div>
            </div>
          )}
          {qualityDropdown.openId && qualityDropdown.pos && (
            <ModelDropdownMenuShell menuRef={qualityDropdown.menuRef} pos={qualityDropdown.pos} emptyText="">
              {[
                <ModelDropdownSectionHeader key="hdr" label={MOTION_QUALITY_HEADING} />,
                ...wholeQualityOptions.map((q) => {
                  const credits = creditsFor(q);
                  // ライトの料金の行が未設定 (credits.wholeSpeed が null) なら選べない
                  const unavailable = !wholeQualities.includes(q);
                  return (
                    <div key={q} data-testid={`motion-whole-quality-${q}`} data-active={wholeQuality === q ? 'true' : 'false'}>
                      <ModelDropdownItem
                        label={(
                          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span>{MOTION_QUALITY_LABELS[q]}</span>
                            <span style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'normal', maxWidth: 220 }}>{MOTION_QUALITY_DESCRIPTIONS[q]}</span>
                          </span>
                        )}
                        pts={credits != null ? `${credits}pt` : undefined}
                        active={wholeQuality === q}
                        disabled={unavailable}
                        onSelect={() => {
                          setStoredQuality(q);
                          qualityDropdown.close();
                        }}
                      />
                    </div>
                  );
                }),
              ]}
            </ModelDropdownMenuShell>
          )}
        </div>
      </div>

      <div className={`${styles.expandContent} ${expanded ? styles.expanded : styles.collapsed}`}>
        <div className={styles.bgmBody} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4, paddingBottom: 14 }}>
          {/* 生成前 (BGM モーダル) に決めた拍の設定 (§10.9) は変えられないので何も出さない (利用者指示 2026-10-02) */}
          {hasBgm && plan?.beatSync !== undefined && !plan.beatSyncFixed && (
            // 拍との連動 (尺の割付・レンダラーへの拍の情報・同期の採点) をまとめて切る (利用者要望 2026-09-29)。
            // 生成前に決めていないシナリオ (BGM なしで作って後から付けた) だけここで決める (§10.9)
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
              <ToggleSwitch
                checked={plan.beatSync}
                onChange={(checked) => void handleBeatSyncChange(checked)}
                label={plan.beatSync ? 'BGM の拍に合わせる' : 'BGM の拍に合わせない'}
                disabled={beatSyncSaving || anyRunning || submitting}
              />
              <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.45)' }}>
                {plan.beatSync ? 'シーンの切り替えを小節・拍の切れ目に置きます' : 'ナレーションの長さで尺を割り付け、拍との同期は採点しません'}
              </span>
            </div>
          )}
          {wholeSupported && (
            // 初稿の AI モデル (全経路共通の選択)。「動画を生成」の ▼ ではなくカード内に置く (利用者指示 2026-10-16)
            <MotionModelSelect disabled={anyRunning || submitting} testId="motion-whole-model" />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
            {whole && (
              // 枠線・塗りの無いテキストボタン (利用者指示 2026-09-29)
              <button type="button" disabled={opening} onClick={() => void handlePlayWhole()} style={textButtonStyle(opening)}>
                {opening ? '読み込み中…' : '全編を再生'}
              </button>
            )}
            {whole && handleReviseWholeMotion && (
              <button
                type="button"
                className="btn-modal-secondary-sm"
                data-testid="motion-whole-revise"
                disabled={anyRunning || submitting || reviseRate == null || !!reviseVoiceBlock}
                title={reviseVoiceBlock
                  ?? (reviseRate == null ? '料金が設定されていないため、現在ご利用いただけません' : '全編に修正の指示を出し、各シーンを置き換えます')}
                onClick={() => { setWholeMessage(null); setReviseOpen(true); }}
              >
                AI で修正
              </button>
            )}
            {/* BGM が無くても「尺の割付を更新」は出す (ナレーションの長さと予定尺で秒に割り付ける・2026-09-30) */}
            <button type="button" disabled={replan.busy || anyRunning} onClick={() => void replan.run()} style={linkStyle(replan.busy)}>
              {replan.busy ? '尺の割付を更新中…' : '尺の割付を更新'}
            </button>
          </div>
          {/* 生成中の段階 (初稿 → 試写 → 採点 → 修正 → 本描画) と、版ごとの途中経過 (試写のコマ・採点) */}
          {wholeRunning && (
            <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.7)' }} data-testid="motion-whole-stage">
              {wholeMotionStage?.[scenario.id] || '生成を始めています…'}
            </span>
          )}
          <MotionWholePreviewList previews={plan?.previews ?? null} running={!!wholeRunning} />
          {wholeSupported && blockReason && plan && !shownAdvice && notice?.kind !== 'advice' && (
            <span className={styles.motionSyncStaleNotice}>{blockReason}</span>
          )}
          {wholeMessage && <span className={styles.motionSyncStaleNotice}>{wholeMessage}</span>}
          {whole && reviseVoiceBlock && (
            <span className={styles.motionSyncStaleNotice} data-testid="motion-revise-voice-block">{reviseVoiceBlock}</span>
          )}
          {voiceFirstNotice && !wholeRunning && (
            <span className={styles.motionSyncStaleNotice} data-testid="motion-voice-first-notice">{voiceFirstNotice}</span>
          )}
          {!hasBgm && (
            // BGM なしでも作れる (2026-09-30)。拍に合わせたい場合の案内だけ控えめに出す
            <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.6)' }} data-testid="motion-no-bgm-notice">
              BGM がありません。ナレーションの長さと予定尺で尺を割り付けて生成します (BGM を付けると拍に合わせられます)
            </span>
          )}
          {shownAdvice ? (
            <AdviceBox advice={shownAdvice} />
          ) : notice?.kind === 'advice' ? (
            <span className={styles.motionSyncStaleNotice}>{notice.message}</span>
          ) : notice?.kind === 'stale' ? (
            <span className={styles.motionSyncStaleNotice}>
              尺の割付が古い: {notice.reason}。「尺の割付を更新」で合わせ直せます
            </span>
          ) : notice?.kind === 'fallback' ? (
            <span className={styles.motionSyncStaleNotice}>{notice.reason}</span>
          ) : notice?.kind === 'relaxed' ? (
            // 生成は止めない補足なので、警告色ではなく控えめな文字で出す
            <span style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.6)' }} data-testid="motion-plan-relaxed">{notice.message}</span>
          ) : null}
        </div>
      </div>
      {reviseOpen && (
        <MotionReviseModal
          rate={reviseRate}
          submitting={submitting}
          onSubmit={(instruction) => void handleReviseSubmit(instruction)}
          onClose={() => { if (!submitting) setReviseOpen(false); }}
        />
      )}
    </div>
  );
}
